/**
 * HostBookingsPage Component
 *
 * Main page for managing host bookings with filtering and actions.
 */

import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useHostBookings, useBookingActions } from './hooks';
import { BookingCard, AcceptModal, DeclineModal, CancelModal } from './components';
import { HostLayout } from '../components';
import type { HostBooking } from '../hooks';

type FilterTab = 'all' | 'pending' | 'upcoming' | 'active' | 'past';

const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'active', label: 'In Progress' },
  { id: 'past', label: 'Past' },
];

export const HostBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('filter') as FilterTab) || 'all';

  const { bookings, isLoading, error, refetch } = useHostBookings();
  const {
    acceptBooking,
    declineBooking,
    cancelBooking,
    isProcessing,
  } = useBookingActions();

  // Modal state
  const [acceptModalBooking, setAcceptModalBooking] = useState<HostBooking | null>(null);
  const [declineModalBooking, setDeclineModalBooking] = useState<HostBooking | null>(null);
  const [cancelModalBooking, setCancelModalBooking] = useState<HostBooking | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter bookings
  const filteredBookings = useMemo(() => {
    let filtered = bookings;
    const now = new Date();

    // Apply tab filter
    switch (activeTab) {
      case 'pending':
        filtered = bookings.filter((b) => b.status === 'pending');
        break;
      case 'upcoming':
        filtered = bookings.filter(
          (b) => b.status === 'confirmed' && b.checkIn > now
        );
        break;
      case 'active':
        filtered = bookings.filter((b) => b.status === 'active');
        break;
      case 'past':
        filtered = bookings.filter(
          (b) => b.status === 'completed' || b.status === 'cancelled' || b.status === 'declined'
        );
        break;
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.guestName.toLowerCase().includes(query) ||
          b.listingTitle.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [bookings, activeTab, searchQuery]);

  // Pending count for badge
  const pendingCount = bookings.filter((b) => b.status === 'pending').length;

  const handleTabChange = (tab: FilterTab) => {
    setSearchParams({ filter: tab });
  };

  const handleAccept = async () => {
    if (!acceptModalBooking) return;
    const success = await acceptBooking(acceptModalBooking.id);
    if (success) {
      refetch();
      setAcceptModalBooking(null);
    }
  };

  const handleDecline = async (reason: string, message?: string) => {
    if (!declineModalBooking) return;
    const success = await declineBooking({
      bookingId: declineModalBooking.id,
      reason,
      message,
    });
    if (success) {
      refetch();
      setDeclineModalBooking(null);
    }
  };

  const handleCancel = async (reason: string) => {
    if (!cancelModalBooking) return;
    const success = await cancelBooking({
      bookingId: cancelModalBooking.id,
      reason,
    });
    if (success) {
      refetch();
      setCancelModalBooking(null);
    }
  };

  const handleMessage = (booking: HostBooking) => {
    navigate(`/host/messages/${booking.id}`);
  };

  if (isLoading) {
    return (
      <HostLayout title="Bookings">
        <div className="animate-pulse space-y-4">
          <div className="flex gap-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-200 rounded-full w-24" />
            ))}
          </div>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </HostLayout>
    );
  }

  if (error) {
    return (
      <HostLayout title="Bookings">
        <div className="bg-red-50 text-red-600 p-4 rounded-xl">
          Error loading bookings: {error?.message || 'Unknown error'}
        </div>
      </HostLayout>
    );
  }

  return (
    <HostLayout title="Bookings">
      <div className="max-w-5xl">
      {/* Subtitle */}
      <p className="text-gray-500 -mt-4 mb-6">Manage your booking requests and reservations</p>

      {/* Search and Filter */}
      <div className="mb-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by guest name or property..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.label}
              {tab.id === 'pending' && pendingCount > 0 && (
                <span className="ml-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Booking List */}
      {filteredBookings.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-2xl">
          <CalendarIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
          <p className="text-gray-500">
            {searchQuery
              ? 'Try adjusting your search terms'
              : activeTab === 'pending'
              ? "You don't have any pending booking requests"
              : activeTab === 'upcoming'
              ? "You don't have any upcoming reservations"
              : activeTab === 'active'
              ? "You don't have any guests checked in"
              : "You don't have any bookings yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              onAccept={
                booking.status === 'pending'
                  ? () => setAcceptModalBooking(booking)
                  : undefined
              }
              onDecline={
                booking.status === 'pending'
                  ? () => setDeclineModalBooking(booking)
                  : undefined
              }
              onCancel={
                booking.status === 'confirmed' && booking.checkIn > new Date()
                  ? () => setCancelModalBooking(booking)
                  : undefined
              }
              onMessage={
                booking.status === 'confirmed' || booking.status === 'active'
                  ? () => handleMessage(booking)
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {bookings.length > 0 && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
            <p className="text-sm text-gray-500">Pending</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">
              {bookings.filter((b) => b.status === 'confirmed').length}
            </p>
            <p className="text-sm text-gray-500">Upcoming</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">
              {bookings.filter((b) => b.status === 'active').length}
            </p>
            <p className="text-sm text-gray-500">In Progress</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">
              {bookings.filter((b) => b.status === 'completed').length}
            </p>
            <p className="text-sm text-gray-500">Completed</p>
          </div>
        </div>
      )}

      {/* Modals */}
      {acceptModalBooking && (
        <AcceptModal
          isOpen={true}
          onClose={() => setAcceptModalBooking(null)}
          onAccept={handleAccept}
          isLoading={isProcessing}
          booking={{
            guestName: acceptModalBooking.guestName,
            listingTitle: acceptModalBooking.listingTitle,
            checkIn: acceptModalBooking.checkIn,
            checkOut: acceptModalBooking.checkOut,
            nights: Math.ceil(
              (acceptModalBooking.checkOut.getTime() - acceptModalBooking.checkIn.getTime()) /
                (1000 * 60 * 60 * 24)
            ),
            hostPayout: acceptModalBooking.hostPayout,
          }}
        />
      )}

      {declineModalBooking && (
        <DeclineModal
          isOpen={true}
          onClose={() => setDeclineModalBooking(null)}
          onDecline={handleDecline}
          isLoading={isProcessing}
          guestName={declineModalBooking.guestName}
        />
      )}

      {cancelModalBooking && (
        <CancelModal
          isOpen={true}
          onClose={() => setCancelModalBooking(null)}
          onCancel={handleCancel}
          isLoading={isProcessing}
          booking={{
            guestName: cancelModalBooking.guestName,
            checkIn: cancelModalBooking.checkIn,
            checkOut: cancelModalBooking.checkOut,
            hostPayout: cancelModalBooking.hostPayout,
          }}
        />
      )}
      </div>
    </HostLayout>
  );
};

// Icons
const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

export default HostBookingsPage;
